/**
 * Evals / Savings / Models API client for the Auteur Cloud web app.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05B. This is the browser half
 * of the eval and savings surface; the server half is C02D's
 * `auteur_cloud/evals/**` package (`store.py`, `scorecards.py`, `savings.py`,
 * `routing.py`). Every TypeScript shape below mirrors, field for field, one of
 * C02D's `as_dict()` payloads, and `tests/auteur_cloud/test_eval_savings_ui.py`
 * re-checks that parity against the real Python source at test time.
 *
 * Three required properties this client exists to preserve:
 *
 * NO METRIC WITHOUT A DENOMINATOR/SOURCE
 *   Every figure type carries `state`, `unit`, `numerator`, `denominator` and
 *   `denominator_label` — the exact fields `SavingsFigure.as_dict()` returns.
 *   Nothing in this surface renders a bare number: `MetricCard` prints the
 *   denominator label beside every value, and a figure whose `state` is
 *   `unavailable` or `withheld` cannot carry a value at all (enforced
 *   server-side by `SavingsFigure.__post_init__`, mirrored here by typing
 *   `value` as `number | null` and never coalescing it to 0).
 *
 * OPAQUE CLI RUNS REMAIN OPAQUE
 *   The payloads this client reads are aggregates: scorecards, savings
 *   figures, routing decisions. No type here names a prompt body, a
 *   completion, a transcript, or any per-run content field — because C02D's
 *   `as_dict()` payloads return none, and this client cannot render what it
 *   is never given.
 *
 * RECOMMENDATIONS DO NOT SILENTLY CHANGE EXACT USER REQUESTS
 *   `RoutingDecisionPayload` carries `requested_model`, `exact_request`,
 *   `applied_model`, and `substituted` exactly as `RoutingDecision.as_dict()`
 *   does. The client renders them; it never picks a model. The server type
 *   itself raises `SilentSubstitutionRefused` if an exact request's applied
 *   model differs from the requested one, so a `substituted: true` decision
 *   for an exact request can never arrive.
 *
 * MISSING TELEMETRY RENDERS AS UNAVAILABLE, NEVER ZERO
 *   The four-way evidence state (`measured`, `estimated`, `unavailable`,
 *   `withheld`) is `SAVINGS_DASHBOARD_SPEC.md` §8's taxonomy and C02D's
 *   `SavingsFigure.state` vocabulary. `MetricCard` renders each state with
 *   its own branch; the unavailable/withheld branches show no number.
 *
 * SESSION, NOT TOKEN
 *   Same posture as `api/auth.ts` and `api/billing.ts`: the
 *   `__Host-auteur_session` cookie is the only credential. `request()` above
 *   calls `requestJson` from `./http`, whose one shared `fetch` call sets
 *   `credentials: 'include'` so the browser attaches it — this file does not
 *   repeat that per call. Nothing here reads or writes `localStorage`,
 *   `document.cookie`, or an `Authorization` header.
 *
 * ROUTES
 *   `fetchScorecards`, `fetchSavingsFigures`, and `fetchRoutingDecision` call
 *   `GET /v1/evals/scorecards`, `GET /v1/evals/savings` and
 *   `POST /v1/evals/routing`, served by `auteur_cloud/api/routes/evals.py`
 *   (mounted by Hosted Brain H12; the gap C05B disclosed here is closed).
 *   A deployment without the evals group, or a missing service, still
 *   answers 404/503, and every page then renders an honest `Unavailable`
 *   state — never a fabricated figure.
 *
 * BUILD STATUS — compiled and exercised as part of the Vite customer SPA.
 */

import { ApiClientError, requestJson } from './http';

const BASE = '/v1/evals';

/** The one shape every endpoint here returns on failure. */
export interface EvalsApiErrorBody {
  code: string;
  message: string;
  status: number;
}

/** A structured failure. Mirrors `BillingApiError` in `api/billing.ts`. */
export class EvalsApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'EvalsApiError';
    this.status = status;
    this.code = code;
  }

  /** The endpoint is unavailable in this deployment, or a required service is not configured. */
  get isUnavailable(): boolean {
    return this.status === 503 || this.status === 404;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return (await requestJson(`${BASE}${path}`, init)) as T;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new EvalsApiError(error.status, error.code, error.message);
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Evidence states — SAVINGS_DASHBOARD_SPEC.md §8, C02D's SavingsFigure.state
// ---------------------------------------------------------------------------

/**
 * The four-way evidence state. Deliberately a closed union: a fifth state
 * must be added to C02D's `SavingsFigure` vocabulary first, and the parity
 * test in `test_eval_savings_ui.py` fails if this set and the server's
 * diverge. `unavailable` and `withheld` can never carry a number — that is
 * `SavingsFigure.__post_init__`'s refusal, not a UI convention.
 */
export type MetricState = 'measured' | 'estimated' | 'unavailable' | 'withheld';

// ---------------------------------------------------------------------------
// Savings figures — mirrors `SavingsFigure.as_dict()` field for field
// ---------------------------------------------------------------------------

/**
 * One savings figure, exactly as `auteur_cloud/evals/savings.py`'s
 * `SavingsFigure.as_dict()` returns it. `value` is `number | null` because
 * the server type refuses to attach a number to an `unavailable` or
 * `withheld` figure. `denominator_label` is the human sentence that states
 * what the number is a ratio *of* — the "no metric without a denominator"
 * property lives in that field.
 */
export interface SavingsFigurePayload {
  claim_kind: string;
  state: MetricState;
  unit: string;
  value: number | null;
  numerator: number | null;
  denominator: number | null;
  denominator_label: string;
  sample_count: number;
  excluded_count: number;
  withheld: string[];
  assumptions: string[];
  scope_ref: string | null;
  task_class: string | null;
  detail: Record<string, unknown>;
}

export async function fetchSavingsFigures(projectId?: string): Promise<SavingsFigurePayload[]> {
  const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  const body = await request<{ figures: SavingsFigurePayload[] }>(`/savings${query}`, {
    method: 'GET',
  });
  return body.figures;
}

// ---------------------------------------------------------------------------
// Scorecards — mirrors `Scorecard.as_dict()` field for field
// ---------------------------------------------------------------------------

/**
 * One scorecard row, exactly as `auteur_cloud/evals/scorecards.py`'s
 * `Scorecard.as_dict()` returns it. `raw_score_mean` is `null` when
 * contributing receipts used mixed scales (`raw_scale === 'mixed'`); the
 * normalized mean still publishes, because normalizing is exactly what makes
 * mixed scales comparable. `evidence_weight` is `thin`, `adequate`, or
 * `strong` per C02D's named policy thresholds — a scorecard is labelled with
 * its weight, never presented as equally confident.
 */
export interface ScorecardPayload {
  dimension: string;
  subject_key: string;
  slice: string;
  scorecard_version: number;
  attempts: number;
  trusted_green: number;
  sample_count: number;
  scored_sample_count: number;
  raw_score_mean: number | null;
  raw_scale: string | null;
  normalized_score_mean: number | null;
  trusted_green_rate: number | null;
  total_cost_usd: number | null;
  unpriced_attempts: number;
  cost_per_trusted_green_usd: number | null;
  evidence_weight: string;
  withheld: string[];
  source_digest: string;
}

export async function fetchScorecards(
  dimension: 'model' | 'executor' | 'prompt' | 'repository',
  projectId?: string,
): Promise<ScorecardPayload[]> {
  const query = projectId ? `&project_id=${encodeURIComponent(projectId)}` : '';
  const body = await request<{ scorecards: ScorecardPayload[] }>(
    `/scorecards?dimension=${dimension}${query}`,
    { method: 'GET' },
  );
  return body.scorecards;
}

// ---------------------------------------------------------------------------
// Routing — mirrors `RoutingDecision.as_dict()` / `Recommendation.as_dict()`
// ---------------------------------------------------------------------------

/** Mirrors `auteur_cloud/evals/routing.py`'s `Recommendation.as_dict()`. */
export interface RecommendationPayload {
  subject_key: string;
  normalized_score: number;
  trusted_green_rate: number | null;
  sample_count: number;
  evidence_weight: string;
  rationale: string;
  margin: number;
}

/**
 * Mirrors `RoutingDecision.as_dict()`. The client renders this; it never
 * edits it. An exact request always renders `requested_model` as the model
 * that runs — the server type makes any other construction unrepresentable
 * (`SilentSubstitutionRefused`), and `ModelsPage` displays any advisory as a
 * labelled, dismissable suggestion only.
 */
export interface RoutingDecisionPayload {
  requested_model: string;
  exact_request: boolean;
  applied_model: string;
  substituted: boolean;
  status: string;
  advisory: RecommendationPayload | null;
  reasons: string[];
}

export async function fetchRoutingDecision(
  requestedModel: string,
  exact: boolean,
): Promise<RoutingDecisionPayload> {
  const body = await request<{ decision: RoutingDecisionPayload }>('/routing', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ requested_model: requestedModel, exact }),
  });
  return body.decision;
}

/**
 * The complete set of eval operations available to this client, exported as
 * data so the contract test can assert the set rather than grep for
 * absences — same pattern as `billing.ts`'s `BILLING_API_OPERATIONS`.
 */
export const EVALS_API_OPERATIONS = Object.freeze([
  'savings.read',
  'scorecards.read',
  'routing.decide',
]);
